import PropTypes from 'prop-types';
import { graphql, createRefetchContainer } from 'react-relay/legacy';

// components
import ShippingIcon from 'dibs-icons/exports/Shipping';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import { shipsFrom, shipsFromLocation } from '../messages/pdpBlurbMessages';
import { PdpSharedShippingInfo } from '../PdpSharedShippingInfo/PdpSharedShippingInfo';
import { PdpSharedShippingInfoLink } from '../PdpSharedShippingInfo/PdpSharedShippingInfoLink';
import { SeoLink, PARTIALLY_LINKABLE_LINK } from 'dibs-seo/exports/SeoLink';
import { PdpSharedShippingHolidayDelivery } from '../PdpSharedShippingHolidayDelivery/PdpSharedShippingHolidayDelivery';

// helpers
import {
    getUserSessionCountryCode,
    useUserSessionRegions,
} from 'dibs-regional-info/exports/regionalInfoHelpers';

// styles
import styles from './PdpRespSidebarBlurbShipping.scss';
export const PdpRespSidebarBlurbShippingComponent = ({
    item,
    itemId,
    viewer,
    currency,
    userId,
    user,
    shipmentQuote,
    shipment,
    renderTooltip,
    title,
    relay,
    sku,
    isClient,
}) => {
    const doRefetch = refetchVariables => {
        const variables = { ...refetchVariables, userCountryCode: getUserSessionCountryCode() };
        relay.refetch(variables);
    };

    const userSessionRegions = useUserSessionRegions();

    const { relevantShipping, regionalInfo, pdpHolidayShippingFeatureFlag } = viewer || {};
    const isVerifiedTrade = user?.isVerifiedTrade || false;
    const itemLocation = item?.address?.itemAddress;
    const isGalleryLocation = item?.address?.isGalleryRoomAddress;
    const leadTime = item?.attributes?.leadTime?.[0]?.option?.urlLabel;
    const quoteRequestStatus = relevantShipping?.quoteRequestStatus;
    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';

    if (!regionalInfo && !userSessionRegions) {
        return null;
    }

    const itemShipsFrom = isGalleryLocation && isVerifiedTrade ? `${itemLocation}.` : itemLocation;

    return (
        <>
            <PdpSharedBlurb IconComponent={ShippingIcon} iconClass={styles.shippingIcon}>
                {title}
                <PdpSharedShippingInfo
                    item={item}
                    regionalInfo={regionalInfo || null}
                    currency={currency}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                    leadTime={leadTime}
                    quoteRequestStatus={quoteRequestStatus}
                    renderTooltip={renderTooltip}
                    noLineBreaks
                    user={user}
                >
                    <PdpSharedShippingInfoLink
                        shipmentQuote={shipmentQuote}
                        currency={currency}
                        userId={userId}
                        item={item}
                        itemId={itemId}
                        user={user}
                        viewer={viewer}
                        showQuoteDetails={showQuoteDetails}
                        quoteRequestStatus={quoteRequestStatus}
                        onSuccessfulQuoteRequest={doRefetch}
                        displayInline
                    />
                    <div className={styles.shipsFromContainer}>
                        <span className={styles.shipsFrom}>{shipsFrom}</span>
                        {item?.locationBrowseUrl?.path ? (
                            <SeoLink
                                linkData={item.locationBrowseUrl}
                                variant={PARTIALLY_LINKABLE_LINK}
                            >
                                {shipsFromLocation(itemShipsFrom)}
                            </SeoLink>
                        ) : (
                            <span>{shipsFromLocation(itemShipsFrom)}</span>
                        )}
                    </div>
                </PdpSharedShippingInfo>
            </PdpSharedBlurb>
            {pdpHolidayShippingFeatureFlag && (
                <PdpSharedShippingHolidayDelivery
                    useIcon
                    sku={sku}
                    item={item}
                    viewer={viewer}
                    useTooltipDetails
                    isClient={isClient}
                />
            )}
        </>
    );
};

PdpRespSidebarBlurbShippingComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    relay: PropTypes.object.isRequired,
    renderTooltip: PropTypes.bool,
    title: PropTypes.object,
    sku: PropTypes.object,
    isClient: PropTypes.bool,
};

export const PdpRespSidebarBlurbShipping = createRefetchContainer(
    PdpRespSidebarBlurbShippingComponent,
    {
        viewer: graphql`
            fragment PdpRespSidebarBlurbShipping_viewer on Viewer {
                pdpHolidayShippingFeatureFlag: featureFlag(feature: "pdpHolidayShipping")
                ...PdpSharedShippingInfoLink_viewer
                ...PdpSharedShippingHolidayDelivery_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                @include(if: $fetchRegionalInfo) {
                    ...PdpSharedShippingInfo_regionalInfo
                }
            }
        `,
        user: graphql`
            fragment PdpRespSidebarBlurbShipping_user on User {
                isVerifiedTrade
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingInfo_user
            }
        `,
        item: graphql`
            fragment PdpRespSidebarBlurbShipping_item on Item {
                ...PdpSharedShippingInfo_item
                ...PdpSharedShippingInfoLink_item
                ...PdpSharedShippingHolidayDelivery_item
                hasShipmentQuotes
                hasPreQuotedShipping
                itemIsOnHold: isOnHold(buyerId: $userId)
                locationBrowseUrl {
                    ...SeoLink_linkData
                }
                address {
                    itemAddress: displayCityStateCountry(fullCountryName: true)
                    isGalleryRoomAddress
                }
                attributes {
                    leadTime {
                        option {
                            urlLabel
                        }
                    }
                }
            }
        `,
        shipmentQuote: graphql`
            fragment PdpRespSidebarBlurbShipping_shipmentQuote on ShipmentQuote {
                isCustom
                isNew
                status
                buyerTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                serviceMethod {
                    isWhiteGlove
                }
                ...PdpSharedShippingInfoLink_shipmentQuote
                ...PdpSharedShippingInfo_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpRespSidebarBlurbShipping_shipment on Shipment {
                ...PdpSharedShippingInfo_shipment
            }
        `,
        sku: graphql`
            fragment PdpRespSidebarBlurbShipping_sku on ItemSkuType {
                ...PdpSharedShippingHolidayDelivery_sku
            }
        `,
    },
    graphql`
        query PdpRespSidebarBlurbShippingRefetchQuery(
            $userId: String!
            $fetchUser: Boolean!
            $itemId: String!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $isClient: Boolean!
        ) {
            viewer {
                ...PdpRespSidebarBlurbShipping_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                    shipmentQuote {
                        ...PdpRespSidebarBlurbShipping_shipmentQuote
                    }
                    shipment {
                        ...PdpRespSidebarBlurbShipping_shipment
                    }
                }
            }
        }
    `
);
